class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.mainImages = Array.from(this.querySelectorAll('.product-gallery__main-image'));
    this.thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.setActiveIndex(Number(thumb.dataset.index)));
    });

    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  onVariantChange(event) {
    const { imageId } = event.detail;
    if (!imageId) return;
    const index = this.mainImages.findIndex((img) => img.dataset.imageId === String(imageId));
    if (index > -1) this.setActiveIndex(index);
  }

  setActiveIndex(index) {
    this.mainImages.forEach((img, i) => img.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }
}

customElements.define('product-gallery', ProductGallery);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim()) {
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.remove();
        }
      })
      .catch(() => this.remove());
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variant-json]').textContent);
    this.input = this.querySelector('.variant-picker__variant-id');
    this.addToCartButton = document.querySelector('[data-add-to-cart]');
    this.priceBlocks = Array.from(document.querySelectorAll('[data-variant-price]'));

    this.querySelectorAll('.variant-swatch').forEach((swatch) => {
      swatch.addEventListener('click', () => this.selectSwatch(swatch));
    });
  }

  selectSwatch(swatch) {
    const group = swatch.closest('.variant-picker__values');
    group.querySelectorAll('.variant-swatch').forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    swatch.classList.add('is-selected');
    swatch.setAttribute('aria-pressed', 'true');
    this.updateVariant();
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('.variant-picker__option'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((group) => group.querySelector('.variant-swatch.is-selected')?.dataset.value);
  }

  updateVariant() {
    const selected = this.getSelectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));

    this.togglePrice(variant);
    this.toggleAddToCart(variant);
    this.input.value = variant ? variant.id : '';

    if (variant) {
      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: { imageId: variant.featured_image ? variant.featured_image.id : null },
        })
      );
    }
  }

  togglePrice(variant) {
    this.priceBlocks.forEach((block) => {
      block.hidden = !variant || block.dataset.variantPrice !== String(variant.id);
    });
  }

  toggleAddToCart(variant) {
    if (!this.addToCartButton) return;
    const label = this.addToCartButton.querySelector('.product-form__submit-label') || this.addToCartButton;

    if (!variant) {
      this.addToCartButton.disabled = true;
      label.textContent = this.addToCartButton.dataset.labelUnavailable;
    } else if (!variant.available) {
      this.addToCartButton.disabled = true;
      label.textContent = this.addToCartButton.dataset.labelSoldOut;
    } else {
      this.addToCartButton.disabled = false;
      label.textContent = this.addToCartButton.dataset.labelAvailable;
    }
  }
}

customElements.define('variant-picker', VariantPicker);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-add-to-cart]');
    this.label = this.button.querySelector('.product-form__submit-label') || this.button;
    this.errorEl = this.querySelector('[data-add-to-cart-error]');
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  onSubmit(event) {
    event.preventDefault();
    if (this.button.disabled) return;

    const formData = new FormData(this.form);
    this.button.disabled = true;
    this.label.textContent = this.button.dataset.labelAdding;
    this.hideError();

    fetch(this.dataset.cartAddUrl, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          // Shopify's own error message (eg. "no hay más existencias", variante inválida) —
          // showing it beats silently reverting the button, which looks identical to the
          // click doing nothing at all.
          this.showError(data.description || data.message);
          this.label.textContent = this.button.dataset.labelAvailable;
          this.button.disabled = false;
          return;
        }

        this.label.textContent = this.button.dataset.labelAdded;
        // The cart drawer (assets/cart.js, global) listens for this to refresh its content
        // and open itself — same event a quantity/remove change in the drawer itself fires,
        // so there's one single "cart changed" signal the whole page reacts to.
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

        setTimeout(() => {
          this.label.textContent = this.button.dataset.labelAvailable;
          this.button.disabled = false;
        }, 2000);
      })
      .catch((error) => {
        this.showError(error && error.message);
        this.label.textContent = this.button.dataset.labelAvailable;
        this.button.disabled = false;
      });
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.errorEl.dataset.genericError || 'Error';
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }
}

customElements.define('product-form', ProductForm);
